import {
  filter,
  get,
  includes,
  isArray,
  isBoolean,
  isUndefined,
  keys,
  map,
  values,
} from 'lodash-es'
import {
  AccountType,
  FileMimeType,
  ProjectFileSection,
  ProjectFileUsage,
  ProjectStep,
} from 'src/utils/enums'
import {
  resolveDocumentAccess,
  type DocumentAccessContext,
  type UploadPage,
} from '@autoriz/shared/files'
import {
  // FileMimeTypeExtension,
  ImageFileMimeType,
} from './download'

export type PageFormat = 'a4' | 'a3'

export type FormatDimensions = {
  a4: { width: number; height: number }
  a3: { width: number; height: number }
}

export type ViewerConfig = {
  rename?: boolean
  download?: boolean
  usage?: boolean
  delete?: boolean
  validate?: boolean
  scale?: {
    guide?: {
      width: number
      height: number
    }
    formatGuide?: FormatDimensions
    select?: Array<number>
    transform?: Array<{ src: number; dst: number }>
  }
}

export type EditorConfig = {
  pdf?: {
    to_image?: boolean
  }
  image?: {
    move?: boolean
    rotate?: boolean
    zoom?: boolean
    ratio?: Array<Array<number>> | Array<number>
    maxWidth?: number
    maxHeight?: number
    formatDimensions?: FormatDimensions
  }
}

/**
 * Get viewer guide dimensions for a specific page format.
 * Falls back to static guide if formatGuide is not defined.
 */
export function getViewerGuideForFormat(
  config: ViewerConfig,
  format?: PageFormat
): { width: number; height: number } | undefined {
  if (format && config.scale?.formatGuide) {
    return config.scale.formatGuide[format]
  }
  return config.scale?.guide
}

/**
 * Get editor dimensions for a specific page format.
 * Falls back to static maxWidth/maxHeight if formatDimensions is not defined.
 */
export function getEditorDimensionsForFormat(
  config: EditorConfig,
  format?: PageFormat
): { width: number; height: number } | undefined {
  if (format && config.image?.formatDimensions) {
    return config.image.formatDimensions[format]
  }
  if (config.image?.maxWidth && config.image?.maxHeight) {
    return { width: config.image.maxWidth, height: config.image.maxHeight }
  }
  return undefined
}
//TODO Missing requirements or alerts
export type ProjectFileSource = 'project' | 'urban_analysis'

export type { UploadPage }

/**
 * Presentation config for a document usage.
 *
 * Access — who may see it, who may upload to it, how many files it expects and
 * from which pages — is NOT here: it resolves from the shared rule model in
 * `@autoriz/shared/files` (`resolveDocumentAccess`), whose scope is
 * role × step × status × context. Use the helpers at the bottom of this file.
 */
export type ProjectFileUsageMetaContent = {
  title: string
  description: string
  section: string
  url: string
  // Legacy per-usage MIME allowlist, still consulted by the matrix image
  // selector via `isFileTypeUploadAllowed`. This is the one access-adjacent
  // facet the rule model does not own yet: it predates
  // `USAGE_ALLOWED_MIME_TYPES` in @autoriz/shared and uses a different MIME
  // vocabulary (`FileMimeType` / `ImageFileMimeType`), so reconciling the two
  // changes what uploads validate and deserves its own change.
  upload: Array<string> | boolean
  // Where the files for this usage live. Defaults to `project` (stored in
  // `project_files`). UA-delivered documents (cadastre, PLU, risk reports, …)
  // are read from `urban_analysis_files` and are not user-editable from the
  // documents page.
  source?: ProjectFileSource
  viewer?: ViewerConfig
  editor?: EditorConfig
}

const DEFAULT_SCALE_SELECT_OPTIONS = [100, 125, 150, 200, 250, 500, 1000]
const DEFAULT_SCALE_TRANSFORM_OPTIONS: Array<{ src: number; dst: number }> = [
  { src: 100, dst: 125 },
]

const FACADE_VIEWER_CONFIG: ViewerConfig = {
  usage: false,
  scale: {
    select: DEFAULT_SCALE_SELECT_OPTIONS,
    transform: DEFAULT_SCALE_TRANSFORM_OPTIONS,
    guide: { width: 780, height: 430 },
    formatGuide: {
      a4: { width: 780, height: 400 },
      a3: { width: 740, height: 430 },
    },
  },
}

const GROUND_PLAN_VIEWER_CONFIG: ViewerConfig = {
  usage: false,
  scale: {
    select: DEFAULT_SCALE_SELECT_OPTIONS,
    transform: DEFAULT_SCALE_TRANSFORM_OPTIONS,
    guide: { width: 780, height: 900 },
    formatGuide: {
      a4: { width: 780, height: 900 },
      a3: { width: 1500, height: 920 },
    },
  },
}

const PICTURES_VIEWER_CONFIG: ViewerConfig = {
  usage: false,
  scale: {
    select: DEFAULT_SCALE_SELECT_OPTIONS,
    transform: DEFAULT_SCALE_TRANSFORM_OPTIONS,
    guide: { width: 329, height: 219 },
    formatGuide: {
      a4: { width: 329, height: 219 },
      a3: { width: 490, height: 327 },
    },
  },
}

// Editor configs for plan images — dimensions match the max of A4/A3 matrix placeholders
const GROUND_PLAN_EDITOR_CONFIG: EditorConfig = {
  image: {
    move: true,
    zoom: false,
    maxWidth: 1500,
    maxHeight: 920,
    formatDimensions: {
      a4: { width: 780, height: 900 },
      a3: { width: 1500, height: 920 },
    },
  },
}

const FACADE_EDITOR_CONFIG: EditorConfig = {
  image: {
    move: true,
    zoom: false,
    maxWidth: 780,
    maxHeight: 430,
    formatDimensions: {
      a4: { width: 780, height: 400 },
      a3: { width: 740, height: 430 },
    },
  },
}

const FIELD_CUT_EDITOR_CONFIG: EditorConfig = {
  image: {
    move: true,
    zoom: false,
    maxWidth: 1500,
    maxHeight: 920,
    formatDimensions: {
      a4: { width: 780, height: 900 },
      a3: { width: 1500, height: 920 },
    },
  },
}

const LOCATION_PLAN_EDITOR_CONFIG: EditorConfig = {
  image: {
    move: true,
    zoom: false,
    maxWidth: 750,
    maxHeight: 870,
    formatDimensions: {
      a4: { width: 600, height: 400 },
      a3: { width: 750, height: 870 },
    },
  },
}

const PERSPECTIVE_EDITOR_CONFIG: EditorConfig = {
  image: {
    move: true,
    zoom: true,
    maxWidth: 750,
    maxHeight: 870,
    formatDimensions: {
      a4: { width: 650, height: 450 },
      a3: { width: 750, height: 870 },
    },
  },
}

const PICTURES_EDITOR_CONFIG: EditorConfig = {
  image: {
    maxWidth: 490,
    maxHeight: 327,
    formatDimensions: {
      a4: { width: 329, height: 219 },
      a3: { width: 490, height: 327 },
    },
  },
}

// TODO(#240-follow-up): the shared `ProjectFileUsage` catalogue gained members
// during feature 240 reconciliation (URBAN_ANALYSIS_MAP, LOCATION_PLAN,
// AERIAL_VIEW, CURRENT_MAIN_FACADE, FUTUR_MAIN_FACADE, CURRENT_REAR_FACADE,
// FUTUR_REAR_FACADE, CURRENT_GABLE_1/2, FUTUR_GABLE_1/2, SKETCH, OVERVIEW,
// COMPLEMENTARY_FILE). This UI-meta record covers only the subset that existed
// in the old frontend enum — typed as Partial so callers treat lookups as
// potentially undefined. Runtime behaviour is unchanged. Downstream work:
// decide whether each new usage deserves its own meta entry or should stay
// excluded from UI listings.
export const ProjectFileUsageMeta: Partial<
  Record<ProjectFileUsage, ProjectFileUsageMetaContent>
> = {
  CERFA_DP: {
    title: 'Cerfa DP',
    description: 'Description',
    section: ProjectFileSection.ADMINISTRATIVE,
    upload: [FileMimeType.PDF],
    url: '/documents',
  },
  CERFA_PC: {
    title: 'Cerfa PC',
    description: 'Description',
    section: ProjectFileSection.ADMINISTRATIVE,
    upload: [FileMimeType.PDF],
    url: '/documents',
  },
  CERFA_PCM: {
    title: 'Cerfa PCM',
    description: 'Description',
    section: ProjectFileSection.ADMINISTRATIVE,
    upload: [FileMimeType.PDF],
    url: '/documents',
  },
  CERFA_13703_11: {
    title: 'Cerfa DP (ancien)',
    description: 'Description',
    section: ProjectFileSection.ADMINISTRATIVE,
    upload: [FileMimeType.PDF],
    url: '/documents',
  },
  CERFA_13411_11: {
    title: 'Cerfa PCM (ancien)',
    description: 'Description',
    section: ProjectFileSection.ADMINISTRATIVE,
    upload: [FileMimeType.PDF],
    url: '/documents',
  },
  CERFA_13406_12: {
    title: 'Cerfa PC (ancien)',
    description: 'Description',
    section: ProjectFileSection.ADMINISTRATIVE,
    upload: [FileMimeType.PDF],
    url: '/documents',
  },
  OTHER_APPLICANTS_SHEET: {
    title: 'Fiche autre demandeurs',
    description: 'Description',
    section: ProjectFileSection.ADMINISTRATIVE,
    upload: true,
    url: '/documents',
  },
  PROJECT_SHEET: {
    title: 'Fiche de projet',
    section: ProjectFileSection.ADMINISTRATIVE,
    description: 'Description',
    upload: true,
    url: '/documents',
  },
  CADASTRAL_MAP: {
    title: 'Plan cadastral',
    description: 'Description',
    section: ProjectFileSection.TOWN_PLANNING,
    upload: true,
    source: 'urban_analysis',
    url: '/documents',
  },
  HISTORICAL_MONUMENT_MAP: {
    title: 'Plan monument historique',
    description: 'Description',
    section: ProjectFileSection.TOWN_PLANNING,
    upload: true,
    source: 'urban_analysis',
    url: '/documents',
  },
  PLU: {
    title: 'PLU',
    description: 'Description',
    section: ProjectFileSection.TOWN_PLANNING,
    upload: true,
    source: 'urban_analysis',
    url: '/documents',
  },
  PPRI: {
    title: 'PPRI',
    description: 'Description',
    section: 'TOWN_PLANNING',
    upload: true,
    source: 'urban_analysis',
    url: '/documents',
  },
  AUTHORIZED_COLOR: {
    title: 'Coloris autorisé',
    description: 'Description',
    section: 'TOWN_PLANNING',
    upload: true,
    source: 'urban_analysis',
    url: '/documents',
  },
  GEOGRAPHIC_RISK_REPORT: {
    title: 'Rapport géorisque',
    description: 'Description',
    section: 'TOWN_PLANNING',
    upload: true,
    source: 'urban_analysis',
    url: '/documents',
  },
  URBAN_ANALYSIS: {
    title: 'Etude urbaine',
    description: 'Description',
    section: 'TOWN_PLANNING',
    upload: false,
    source: 'urban_analysis',
    url: '/documents',
  },
  PC_MATRIX: {
    title: 'Matrice PC',
    description: 'Description',
    section: ProjectFileSection.ADMINISTRATIVE,
    upload: [FileMimeType.PDF], //TODO Multiusagesection
    url: '/documents',
  },
  PCMI1: {
    title: 'PCMI 1',
    description: 'Description',
    section: ProjectFileSection.ADMINISTRATIVE,
    upload: false, //TODO Multiusagesection
    url: '/documents',
  },
  PCMI2: {
    title: 'PCMI 2',
    description: 'Description',
    section: ProjectFileSection.ADMINISTRATIVE,
    upload: false, //TODO Multiusagesection
    url: '/documents',
  },
  PCMI2_1: {
    title: 'PCMI 2.1',
    description: 'Description',
    section: ProjectFileSection.ADMINISTRATIVE,
    upload: false, //TODO Multiusagesection
    url: '/documents',
  },
  PCMI3: {
    title: 'PCMI 3',
    description: 'Description',
    section: ProjectFileSection.ADMINISTRATIVE,
    upload: false, //TODO Multiusagesection
    url: '/documents',
  },
  PCMI4: {
    title: 'PCMI 4',
    description: 'Description',
    section: ProjectFileSection.ADMINISTRATIVE,
    upload: false, //TODO Multiusagesection
    url: '/documents',
  },
  PCMI5_1: {
    title: 'PCMI 5',
    description: 'Description',
    section: ProjectFileSection.ADMINISTRATIVE,
    upload: false, //TODO Multiusagesection
    url: '/documents',
  },
  PCMI5_2: {
    title: 'PCMI 5',
    description: 'Description',
    section: ProjectFileSection.ADMINISTRATIVE,
    upload: false, //TODO Multiusagesection
    url: '/documents',
  },
  PCMI5_3: {
    title: 'PCMI 5',
    description: 'Description',
    section: ProjectFileSection.ADMINISTRATIVE,
    upload: false, //TODO Multiusagesection
    url: '/documents',
  },
  PCMI5_4: {
    title: 'PCMI 5',
    description: 'Description',
    section: ProjectFileSection.ADMINISTRATIVE,
    upload: false, //TODO Multiusagesection
    url: '/documents',
  },
  PCMI6: {
    title: 'PCMI 6',
    description: 'Description',
    section: ProjectFileSection.ADMINISTRATIVE,
    upload: false, //TODO Multiusagesection
    url: '/documents',
  },
  PCMI78: {
    title: 'PCMI 7 et 8',
    description: 'Description',
    section: ProjectFileSection.ADMINISTRATIVE,
    upload: false, //TODO Multiusagesection
    url: '/documents',
  },
  DP_MATRIX: {
    title: 'Matrice DP',
    description: 'Description',
    section: ProjectFileSection.ADMINISTRATIVE,
    upload: [FileMimeType.PDF], //TODO Multiusagesection
    url: '/documents',
  },
  DP1: {
    title: 'DP 1',
    description: 'Description',
    section: ProjectFileSection.ADMINISTRATIVE,
    upload: false, //TODO Multiusagesection
    url: '/documents',
  },
  DP2: {
    title: 'DP 2',
    description: 'Description',
    section: ProjectFileSection.ADMINISTRATIVE,
    upload: false, //TODO Multiusagesection
    url: '/documents',
  },
  DP2_1: {
    title: 'DP 2',
    description: 'Description',
    section: ProjectFileSection.ADMINISTRATIVE,
    upload: false, //TODO Multiusagesection
    url: '/documents',
  },
  DP3: {
    title: 'DP 3',
    description: 'Description',
    section: ProjectFileSection.ADMINISTRATIVE,
    upload: false, //TODO Multiusagesection
    url: '/documents',
  },
  DP4_1: {
    title: 'DP 4',
    description: 'Description',
    section: ProjectFileSection.ADMINISTRATIVE,
    upload: false, //TODO Multiusagesection
    url: '/documents',
  },
  DP4_2: {
    title: 'DP 4',
    description: 'Description',
    section: ProjectFileSection.ADMINISTRATIVE,
    upload: false, //TODO Multiusagesection
    url: '/documents',
  },
  DP4_3: {
    title: 'DP 4',
    description: 'Description',
    section: ProjectFileSection.ADMINISTRATIVE,
    upload: false, //TODO Multiusagesection
    url: '/documents',
  },
  DP4_4: {
    title: 'DP 4',
    description: 'Description',
    section: ProjectFileSection.ADMINISTRATIVE,
    upload: false, //TODO Multiusagesection
    url: '/documents',
  },
  DP6: {
    title: 'DP 5',
    description: 'Description',
    section: ProjectFileSection.ADMINISTRATIVE,
    upload: false, //TODO Multiusagesection
    url: '/documents',
  },
  DP78: {
    title: 'DP 7 et 8',
    description: 'Description',
    section: ProjectFileSection.ADMINISTRATIVE,
    upload: false, //TODO Multiusagesection
    url: '/documents',
  },
  DP11: {
    title: 'DP 11',
    description: 'Description',
    section: ProjectFileSection.ADMINISTRATIVE,
    upload: false, //TODO Multiusagesection
    url: '/documents',
  },
  GROUND_PLAN: {
    title: 'Plan masse',
    description: 'Description',
    section: 'DESIGN',
    upload: ImageFileMimeType,
    viewer: {
      scale: {
        select: DEFAULT_SCALE_SELECT_OPTIONS,
        guide: {
          width: 700,
          height: 900,
        },
      },
    },
    editor: GROUND_PLAN_EDITOR_CONFIG,
    url: '/documents',
  },
  CURRENT_GROUND_PLAN: {
    title: 'Plan masse état actuel',
    description: 'Description',
    section: 'DESIGN',
    upload: ImageFileMimeType,
    viewer: GROUND_PLAN_VIEWER_CONFIG,
    editor: GROUND_PLAN_EDITOR_CONFIG,
    url: '/documents',
  },
  FUTUR_GROUND_PLAN: {
    title: 'Plan masse état futur',
    description: 'Description',
    section: 'DESIGN',
    upload: ImageFileMimeType,
    viewer: GROUND_PLAN_VIEWER_CONFIG,
    editor: GROUND_PLAN_EDITOR_CONFIG,
    url: '/documents',
  },
  FIELD_CUT: {
    title: 'Coupe du terrain',
    description: 'Description',
    section: 'DESIGN',
    upload: ImageFileMimeType,
    viewer: {
      scale: {
        select: DEFAULT_SCALE_SELECT_OPTIONS,
        guide: { width: 780, height: 900 },
        formatGuide: {
          a4: { width: 780, height: 900 },
          a3: { width: 1500, height: 920 },
        },
      },
    },
    editor: FIELD_CUT_EDITOR_CONFIG,
    url: '/documents',
  },
  CITY_LOCATION_PLAN: {
    title: 'Plan de la ville',
    description: 'Description',
    section: 'DESIGN',
    upload: ImageFileMimeType,
    editor: LOCATION_PLAN_EDITOR_CONFIG,
    url: '/documents',
  },
  DISTRICT_LOCATION_PLAN: {
    title: 'Plan du quartier',
    description: 'Description',
    section: 'DESIGN',
    upload: ImageFileMimeType,
    editor: LOCATION_PLAN_EDITOR_CONFIG,
    url: '/documents',
  },
  CURRENT_FACADE_1: {
    title: 'Image facade 1 actuel',
    description: 'Description',
    section: 'DESIGN',
    upload: ImageFileMimeType,
    viewer: FACADE_VIEWER_CONFIG,
    editor: FACADE_EDITOR_CONFIG,
    url: '/documents',
  },
  CURRENT_FACADE_2: {
    title: 'Image facade 2 actuel',
    description: 'Description',
    section: 'DESIGN',
    upload: ImageFileMimeType,
    viewer: FACADE_VIEWER_CONFIG,
    editor: FACADE_EDITOR_CONFIG,
    url: '/documents',
  },
  CURRENT_FACADE_3: {
    title: 'Image facade 3 actuel',
    description: 'Description',
    section: 'DESIGN',
    upload: ImageFileMimeType,
    viewer: FACADE_VIEWER_CONFIG,
    editor: FACADE_EDITOR_CONFIG,
    url: '/documents',
  },
  CURRENT_FACADE_4: {
    title: 'Image facade 4 actuel',
    description: 'Description',
    section: 'DESIGN',
    upload: ImageFileMimeType,
    viewer: {
      scale: {
        select: DEFAULT_SCALE_SELECT_OPTIONS,
        transform: DEFAULT_SCALE_TRANSFORM_OPTIONS,
        guide: {
          width: 600,
          height: 400,
        },
      },
    },
    editor: FACADE_EDITOR_CONFIG,
    url: '/documents',
  },
  FUTUR_FACADE_1: {
    title: 'Image facade 1 futur',
    description: 'Description',
    section: 'DESIGN',
    upload: ImageFileMimeType,
    viewer: FACADE_VIEWER_CONFIG,
    editor: FACADE_EDITOR_CONFIG,
    url: '/documents',
  },
  FUTUR_FACADE_2: {
    title: 'Image facade 2 futur',
    description: 'Description',
    section: 'DESIGN',
    upload: ImageFileMimeType,
    viewer: FACADE_VIEWER_CONFIG,
    editor: FACADE_EDITOR_CONFIG,
    url: '/documents',
  },
  FUTUR_FACADE_3: {
    title: 'Image facade 3 futur',
    description: 'Description',
    section: 'DESIGN',
    upload: ImageFileMimeType,
    viewer: FACADE_VIEWER_CONFIG,
    editor: FACADE_EDITOR_CONFIG,
    url: '/documents',
  },
  FUTUR_FACADE_4: {
    title: 'Image facade 4 futur',
    description: 'Description',
    section: 'DESIGN',
    upload: ImageFileMimeType,
    viewer: FACADE_VIEWER_CONFIG,
    editor: FACADE_EDITOR_CONFIG,
    url: '/documents',
  },
  CURRENT_FACADE_5: {
    title: 'Image facade 1 actuel (complément)',
    description: 'Description',
    section: 'DESIGN',
    upload: ImageFileMimeType,
    viewer: FACADE_VIEWER_CONFIG,
    editor: FACADE_EDITOR_CONFIG,
    url: '/documents',
  },
  CURRENT_FACADE_6: {
    title: 'Image facade 2 actuel (complément)',
    description: 'Description',
    section: 'DESIGN',
    upload: ImageFileMimeType,
    viewer: FACADE_VIEWER_CONFIG,
    editor: FACADE_EDITOR_CONFIG,
    url: '/documents',
  },
  CURRENT_FACADE_7: {
    title: 'Image facade 3 actuel (complément)',
    description: 'Description',
    section: 'DESIGN',
    upload: ImageFileMimeType,
    viewer: FACADE_VIEWER_CONFIG,
    editor: FACADE_EDITOR_CONFIG,
    url: '/documents',
  },
  CURRENT_FACADE_8: {
    title: 'Image facade 4 actuel (complément)',
    description: 'Description',
    section: 'DESIGN',
    upload: ImageFileMimeType,
    viewer: FACADE_VIEWER_CONFIG,
    editor: FACADE_EDITOR_CONFIG,
    url: '/documents',
  },
  FUTUR_FACADE_5: {
    title: 'Image facade 1 futur (complément)',
    description: 'Description',
    section: 'DESIGN',
    upload: ImageFileMimeType,
    viewer: FACADE_VIEWER_CONFIG,
    editor: FACADE_EDITOR_CONFIG,
    url: '/documents',
  },
  FUTUR_FACADE_6: {
    title: 'Image facade 2 futur (complément)',
    description: 'Description',
    section: 'DESIGN',
    upload: ImageFileMimeType,
    viewer: FACADE_VIEWER_CONFIG,
    editor: FACADE_EDITOR_CONFIG,
    url: '/documents',
  },
  FUTUR_FACADE_7: {
    title: 'Image facade 3 futur (complément)',
    description: 'Description',
    section: 'DESIGN',
    upload: ImageFileMimeType,
    viewer: FACADE_VIEWER_CONFIG,
    editor: FACADE_EDITOR_CONFIG,
    url: '/documents',
  },
  FUTUR_FACADE_8: {
    title: 'Image facade 4 futur (complément)',
    description: 'Description',
    section: 'DESIGN',
    upload: ImageFileMimeType,
    viewer: FACADE_VIEWER_CONFIG,
    editor: FACADE_EDITOR_CONFIG,
    url: '/documents',
  },
  PICTURE_1: {
    title: 'Photo 1',
    description: 'Description',
    section: ProjectFileSection.DESIGN,
    upload: ImageFileMimeType,
    viewer: PICTURES_VIEWER_CONFIG,
    editor: PICTURES_EDITOR_CONFIG,
    url: '/documents',
  },
  PICTURE_2: {
    title: 'Photo 2',
    description: 'Description',
    section: ProjectFileSection.DESIGN,
    upload: ImageFileMimeType,
    viewer: PICTURES_VIEWER_CONFIG,
    editor: PICTURES_EDITOR_CONFIG,
    url: '/documents',
  },
  PICTURE_3: {
    title: 'Photo 3',
    description: 'Description',
    section: ProjectFileSection.DESIGN,
    upload: ImageFileMimeType,
    viewer: PICTURES_VIEWER_CONFIG,
    editor: PICTURES_EDITOR_CONFIG,
    url: '/documents',
  },
  PICTURE_4: {
    title: 'Photo 4',
    description: 'Description',
    section: ProjectFileSection.DESIGN,
    upload: ImageFileMimeType,
    viewer: PICTURES_VIEWER_CONFIG,
    editor: PICTURES_EDITOR_CONFIG,
    url: '/documents',
  },
  PICTURE_5: {
    title: 'Photo 5',
    description: 'Description',
    section: ProjectFileSection.DESIGN,
    upload: ImageFileMimeType,
    viewer: PICTURES_VIEWER_CONFIG,
    editor: PICTURES_EDITOR_CONFIG,
    url: '/documents',
  },
  PICTURE_6: {
    title: 'Photo 6',
    description: 'Description',
    section: ProjectFileSection.DESIGN,
    upload: ImageFileMimeType,
    viewer: PICTURES_VIEWER_CONFIG,
    editor: PICTURES_EDITOR_CONFIG,
    url: '/documents',
  },
  PERSPECTIVE: {
    title: 'Perspective',
    description: 'Description',
    section: 'DESIGN',
    upload: ImageFileMimeType,
    editor: PERSPECTIVE_EDITOR_CONFIG,
    url: '/documents',
  },
  MATERIALS: {
    title: 'Matériaux',
    description: 'Description',
    section: 'DESIGN',
    upload: ImageFileMimeType,
    editor: {
      image: {
        zoom: true,
        maxWidth: 750,
        maxHeight: 400,
        formatDimensions: {
          a4: { width: 650, height: 200 },
          a3: { width: 750, height: 400 },
        },
      },
    },
    url: '/documents',
  },
  CLOSE_PERSPECTIVE: {
    title: 'Perspective rapprochée',
    description: 'Description',
    section: 'DESIGN',
    upload: ImageFileMimeType,
    editor: {
      image: {
        zoom: true,
        maxWidth: 750,
        maxHeight: 400,
        formatDimensions: {
          a4: { width: 750, height: 400 },
          a3: { width: 750, height: 400 },
        },
      },
    },
    url: '/documents',
  },
  VIEW_TOP: {
    title: 'Vue du dessus',
    description: 'Dessin du projet — vue du dessus (JPG, PDF ou Word)',
    section: 'PICTURES',
    upload: true,
    url: '/documents',
  },
  VIEW_RIGHT: {
    title: 'Vue droite',
    description: 'Dessin du projet — vue droite (JPG, PDF ou Word)',
    section: 'PICTURES',
    upload: true,
    url: '/documents',
  },
  VIEW_LEFT: {
    title: 'Vue gauche',
    description: 'Dessin du projet — vue gauche (JPG, PDF ou Word)',
    section: 'PICTURES',
    upload: true,
    url: '/documents',
  },
  VIEW_FRONT: {
    title: 'Vue de face',
    description: 'Dessin du projet — vue de face (JPG, PDF ou Word)',
    section: 'PICTURES',
    upload: true,
    url: '/documents',
  },
  VIEW_3D_INSERTION: {
    title: 'Vue 3D avec insertion',
    description: 'Dessin du projet — vue 3D avec insertion (JPG, PDF ou Word)',
    section: 'PICTURES',
    upload: true,
    url: '/documents',
  },
  CLIENT_PROXY: {
    title: 'Procuration client',
    description: 'Procuration signée par le client — JPG ou PDF uniquement',
    section: 'ADMINISTRATIVE',
    upload: true,
    url: '/documents',
  },
  EXISTING_PLANS: {
    title: "Relevé de l'existant",
    description:
      "Relevé de l'existant : cotes sur photos, plans existants ou relevé dessiné (PDF ou JPG)",
    section: 'PICTURES',
    upload: true,
    url: '/documents',
  },
  IMPLANTATION: {
    title: 'Implantation',
    description: 'Description',
    section: 'DESIGN',
    upload: true,
    url: '/documents',
  },
  HOUSE_PICTURE: {
    title: 'Photos de la maison',
    description: 'Description',
    section: 'PICTURES',
    upload: true,
    editor: PICTURES_EDITOR_CONFIG,
    url: '/documents',
  },
  STREET_PICTURE: {
    title: 'Photos de la rue',
    description: 'Description',
    section: 'PICTURES',
    upload: true,
    editor: PICTURES_EDITOR_CONFIG,
    url: '/documents',
  },
  GARDEN_PICTURE: {
    title: 'Photos du jardin',
    description: 'Description',
    section: 'PICTURES',
    upload: true,
    editor: PICTURES_EDITOR_CONFIG,
    url: '/documents',
  },
  SALES_FILE: {
    title: 'Dossier commercial',
    description: 'Dossier commercial complet du client (JPG, PDF ou Word)',
    section: 'ADMINISTRATIVE',
    upload: true,
    url: '/documents',
  },
  // "Autres documents" — a real catch-all category (NOT the unassigned inbox;
  // unassigned files carry `use = NULL`). The salesman may add/replace these
  // before the project enters production, then they become read-only.
  UNDEFINED: {
    title: 'Autres documents',
    description: 'Description',
    section: 'OTHER_DOCUMENTS',
    upload: true,
    url: '/documents',
  },
  DEPOSIT_RECEIPT: {
    title: 'Récépissé de dépôt',
    description:
      'Récépissé officiel remis par la mairie lors du dépôt du dossier',
    section: ProjectFileSection.ADMINISTRATIVE,
    upload: true,
    url: '/documents',
  },
  INTERNALS: {
    title: 'Autre documents internes',
    description: 'Description',
    section: 'OTHER_DOCUMENTS',
    upload: true,
    url: '/documents',
  },
}


export const getViewerConfig = (fileUsage: ProjectFileUsage) => {
  const config: ViewerConfig = get(
    ProjectFileUsageMeta,
    `${fileUsage}.viewer`,
    {}
  )
  return {
    ...(config as ViewerConfig),
    rename: get(config, 'rename', true),
    download: get(config, 'download', true),
    usage: get(config, 'usage', true),
    delete: get(config, 'delete', true),
    validate: get(config, 'usage', false),
  }
}

export const getEditorConfig = (fileUsage: ProjectFileUsage) => {
  const config = get(ProjectFileUsageMeta, `${fileUsage}.editor`)
  if (isUndefined(config)) {
    return undefined
  }
  return {
    ...(config as EditorConfig),
    image: {
      move: get(config, 'image.move', true) as boolean,
      rotate: get(config, 'image.rotate', true) as boolean,
      zoom: get(config, 'image.zoom', true) as boolean,
      ratio: get(config, 'image.ratio', [
        [16, 9],
        [4, 3],
        [1, 1],
      ]) as Array<Array<number>>,
      maxWidth: get(config, 'image.maxWidth', undefined) as number | undefined,
      maxHeight: get(config, 'image.maxHeight', undefined) as
        | number
        | undefined,
      formatDimensions: get(config, 'image.formatDimensions', undefined) as
        | FormatDimensions
        | undefined,
    },
    pdf: {
      to_image: get(config, 'pdf.to_image', false) as boolean,
    },
  }
}

export const getAllowedFileUsages = (
  accountType: AccountType,
  projectStep: ProjectStep
) => {
  return filter(
    map(keys(ProjectFileUsage), (usage) => ({
      value: usage,
      ...get(ProjectFileUsageMeta, usage),
    })),
    (usage: ProjectFileUsageMetaContent & { value: string }) =>
      isFileUsageVisible(
        accountType,
        projectStep,
        usage.value as ProjectFileUsage
      )
  )
}

export const getAllowedUsageOptionsBySection = (
  accountType: AccountType,
  projectStep: ProjectStep
) => {
  return filter(
    map(keys(ProjectFileSection), (sectionName) => ({
      label: get(ProjectFileSection, sectionName),
      options: map(
        filter(getAllowedFileUsages(accountType, projectStep), {
          section: sectionName,
        }),
        (usageMeta) => ({
          label: usageMeta.title,
          value: usageMeta.value,
        })
      ),
    })),
    (section) => section.options.length > 0
  )
}

// ── Access ──────────────────────────────────────────────────────────────────
// Thin delegates over the shared rule model. `ctx` carries the status/context
// dimensions (the usage's files, the urban analysis, the project type); omit it
// to ask the purely structural question "could this role ever see this slot at
// this step?" — which is what the triage picker and the missing-document alerts
// want, since they deal in slots that are empty by definition.

/** The status/context half of a rule lookup. */
export type FileAccessContext = Omit<
  DocumentAccessContext,
  'accountType' | 'step'
>

export const isFileUsageVisible = (
  accountType: AccountType,
  projectStep: ProjectStep,
  fileUsage: ProjectFileUsage,
  ctx: FileAccessContext = {}
) =>
  resolveDocumentAccess(fileUsage, {
    ...ctx,
    accountType,
    step: projectStep,
  }).visible

export const isFileUsageEditable = (
  accountType: AccountType,
  projectStep: ProjectStep,
  fileUsage: ProjectFileUsage,
  ctx: FileAccessContext = {}
) =>
  resolveDocumentAccess(fileUsage, {
    ...ctx,
    accountType,
    step: projectStep,
  }).editable

/** How many files this usage expects — 0 when nothing is asked of it. */
export const fileUsageRequiredCount = (
  fileUsage: ProjectFileUsage,
  projectType?: string | null
): number =>
  resolveDocumentAccess(fileUsage, projectType ? { projectType } : {}).required

/**
 * True when the application fills this slot itself, or fills it from another
 * page (the matrix). Such a usage contributes no requirement to the documents
 * ledger and offers no upload affordance there.
 */
export const isFileUsageSystemGenerated = (
  fileUsage: ProjectFileUsage
): boolean =>
  !resolveDocumentAccess(fileUsage).uploadPages.includes('documents')

export const isFileSectionVisible = (
  accountType: AccountType,
  projectStep: ProjectStep,
  section: ProjectFileSection
) =>
  filter(
    keys(ProjectFileUsage),
    (key) => get(ProjectFileUsageMeta, `${key}.section`) == section
  ).some((key) =>
    isFileUsageVisible(accountType, projectStep, key as ProjectFileUsage)
  )

export const isFileTypeUploadAllowed = (
  fileUsage: ProjectFileUsage,
  fileType: string,
  page?: UploadPage
) => {
  const meta = ProjectFileUsageMeta[fileUsage]
  if (!meta) return false
  if (page && !resolveDocumentAccess(fileUsage).uploadPages.includes(page)) {
    return false
  }
  const uploadConfig = meta.upload
  if (isBoolean(uploadConfig)) {
    return uploadConfig && includes(values(FileMimeType), fileType)
  } else if (isArray(uploadConfig)) {
    return (
      includes(uploadConfig, fileType) &&
      includes(values(FileMimeType), fileType)
    )
  } else {
    return false
  }
}

export const isUsageUploadAllowedOnPage = (
  fileUsage: ProjectFileUsage,
  page: UploadPage
): boolean => {
  if (!ProjectFileUsageMeta[fileUsage]) return false
  return resolveDocumentAccess(fileUsage).uploadPages.includes(page)
}
